import { personaStorageKey } from './persona'
import { SavedView, ViewConfig } from './view-config'

/**
 * localStorage persistence for user-saved view configs.
 * Prototype-only: the production version is a saved-views table keyed by org.
 */

export const STORAGE_KEY = 'reporting-prototype.savedViews'

/** Shape gate for deserialized entries — corrupt/foreign entries are dropped silently. */
const isSavedView = (v: unknown): v is SavedView => {
  if (typeof v !== 'object' || v === null) return false
  const c = v as Record<string, unknown>
  return (
    typeof c.id === 'string' &&
    typeof c.name === 'string' &&
    (c.kind === 'aggregate' || c.kind === 'list') &&
    Array.isArray(c.filters) &&
    typeof c.chart === 'string' &&
    Array.isArray(c.knobs) &&
    typeof c.basedOn === 'string'
  )
}

export function listSavedViews(): SavedView[] {
  try {
    const raw = localStorage.getItem(personaStorageKey(STORAGE_KEY))
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isSavedView)
  } catch {
    return []
  }
}

export function saveView(config: ViewConfig, name: string): SavedView {
  const saved: SavedView = {
    ...config,
    id: `saved-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    basedOn: 'basedOn' in config ? (config as SavedView).basedOn : config.id,
    createdAt: new Date().toISOString(),
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify([...listSavedViews(), saved])
  )
  return saved
}

/**
 * Overwrite an existing saved view in place (keeps its id, basedOn and
 * createdAt). Returns the updated view, or undefined if no saved view has that
 * id (e.g. trying to overwrite a shipped template — callers should "save as a
 * copy" instead). Mirrors updateSavedDashboard so reports and dashboards behave
 * the same.
 */
export function updateSavedView(
  id: string,
  config: ViewConfig,
  name: string
): SavedView | undefined {
  const list = listSavedViews()
  const existing = list.find((v) => v.id === id)
  if (!existing) return undefined
  const updated: SavedView = {
    ...config,
    id: existing.id,
    name,
    basedOn: existing.basedOn,
    createdAt: existing.createdAt,
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify(list.map((v) => (v.id === id ? updated : v)))
  )
  return updated
}

export function deleteSavedView(id: string): void {
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify(listSavedViews().filter((v) => v.id !== id))
  )
}

export const getSavedView = (id: string): SavedView | undefined =>
  listSavedViews().find((v) => v.id === id)
